import { createProfit, fetchAdminUserProfits } from '@/api/admin';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import type { IAdminProfit } from '@/entities/admin-user.entity';
import { formatProfitUsd } from '@/lib/format-profit';
import { DollarSignIcon, LoaderCircleIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);

type FetchProfitsFn = (
	profileId: number,
	params?: { cursor?: number; limit?: number },
) => Promise<{ profits: IAdminProfit[]; nextCursor: number | null }>;

interface ProfitsTabProps {
	profileId: number;
	nickname: string;
	onProfitCreated?: () => void;
	readOnly?: boolean;
	fetchProfits?: FetchProfitsFn;
}

export default function ProfitsTab({
	profileId,
	nickname,
	onProfitCreated,
	readOnly = false,
	fetchProfits = fetchAdminUserProfits,
}: ProfitsTabProps) {
	const [profits, setProfits] = useState<IAdminProfit[]>([]);
	const [nextCursor, setNextCursor] = useState<number | null>(null);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(true);
	const [isFetching, setIsFetching] = useState(false);
	const [amount, setAmount] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const fetchingRef = useRef(false);
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	const parsedAmount = Number(amount);
	const isValidAmount = amount && !isNaN(parsedAmount) && parsedAmount > 0;

	async function loadProfits(cursor?: number, reset = false) {
		const { profits: data, nextCursor: newCursor } = await fetchProfits(profileId, { cursor, limit: 50 });
		setProfits(prev => (reset ? data : [...prev, ...data]));
		setNextCursor(newCursor);
		setHasMore(newCursor !== null);
	}

	useEffect(() => {
		loadProfits(undefined, true).finally(() => setLoading(false));
	}, [profileId]);

	async function loadMore() {
		if (fetchingRef.current || !hasMore || nextCursor === null) return;
		fetchingRef.current = true;
		setIsFetching(true);
		try {
			await loadProfits(nextCursor);
		} finally {
			setIsFetching(false);
			fetchingRef.current = false;
		}
	}

	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(entries => {
			if (entries[0]?.isIntersecting) loadMore();
		});
		observer.observe(el);
		return () => observer.disconnect();
	});

	async function handleSubmit() {
		if (!isValidAmount) return;
		setIsSubmitting(true);
		try {
			await createProfit(nickname, parsedAmount);
			toast.success('Профит успешно поставлен');
			setAmount('');
			await loadProfits(undefined, true);
			onProfitCreated?.();
		} finally {
			setIsSubmitting(false);
		}
	}

	if (loading) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	return (
		<div className='flex flex-col gap-3'>
			{!readOnly ? (
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-3'>
					<div className='flex items-center gap-2'>
						<div className='rounded-lg bg-chart-5/15 p-1.5'>
							<DollarSignIcon className='text-chart-5' size={16} />
						</div>
						<CardTitle className='text-sm text-chart-5'>Поставить профит</CardTitle>
					</div>
				</CardHeader>
				<LoadingWrapper show={isSubmitting}>
					<CardContent className='px-4 py-3 flex flex-col gap-2'>
						<Label className='text-sm'>Сумма (USD)</Label>
						<InputGroup>
							<InputGroupAddon>
								<InputGroupText>$</InputGroupText>
							</InputGroupAddon>
							<InputGroupInput
								type='number'
								placeholder='0.00'
								value={amount}
								onChange={e => setAmount(e.target.value)}
							/>
						</InputGroup>
					</CardContent>
					<CardFooter className='border-t px-4 py-3'>
						<Button className='w-full' disabled={!isValidAmount} onClick={handleSubmit}>
							Поставить
						</Button>
					</CardFooter>
				</LoadingWrapper>
			</Card>
			) : null}

			{profits.length === 0 ? (
				<div className='text-center text-muted-foreground text-sm py-4'>История профитов пуста</div>
			) : (
				<div className='flex flex-col gap-2'>
					{profits.map(profit => (
						<div key={profit.id} className='p-3 bg-card rounded-md border border-border flex items-center justify-between gap-2'>
							<span className='font-semibold tabular-nums text-chart-5'>${formatProfitUsd(profit.amount)}</span>
							<span className='text-sm text-muted-foreground'>
								<TimeAgo date={profit.createdAt} formatter={formatter} />
							</span>
						</div>
					))}
					<div ref={sentinelRef} className='h-1' />
					{isFetching && (
						<div className='flex justify-center py-2'>
							<LoaderCircleIcon className='animate-spin text-muted-foreground' size={18} />
						</div>
					)}
				</div>
			)}
		</div>
	);
}
